///
/// EarthBrowser
///
/// Created by Matt Giger on 10/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Tier 1 (first-party, cookieless) metrics transport. Queues typed events
/// and ships them in batches to POST /api/metrics via sendBeacon so the
/// final flush survives tab close. Carries no identifiers: a random
/// per-page-load session id is the only correlation key, and it is never
/// persisted. Store-free and PostHog-free on purpose so the /embed entry
/// can use it; the main app layers PostHog and redux hooks on top in
/// utils/analytics.ts.
///
/// Design: documentation/planning/ANALYTICS_STRATEGY.md §4.2
///

import type {
	AnalyticsBatch,
	AnalyticsEventName,
	AnalyticsEventProps,
	AnalyticsPlatform,
	AnalyticsWireEvent,
} from "@shared-types"
import { cAnalyticsLimits } from "@shared-types"

const cFlushIntervalMs = 15_000
const cFlushAtCount = 20
const cMaxQueued = 200

export interface AnalyticsCoreOptions {
	platform: AnalyticsPlatform
	appVersion: string
	/** Base URL of the API ("" for same-origin) */
	apiUrl: string
	/** When true, events are logged to the console as well as sent */
	debug?: boolean
	/** When false nothing is sent (dev opt-out, `?metrics=off`) */
	enabled?: boolean
}

export class AnalyticsCore {
	readonly sessionId: string = crypto.randomUUID()
	private readonly queue: AnalyticsWireEvent[] = []
	private readonly endpoint: string
	private timer: ReturnType<typeof setTimeout> | null = null
	private subject: string | null = null
	private readonly listeners = new Set<(event: AnalyticsWireEvent) => void>()
	// Dev diagnostics (window.__ebDebug.getMetrics())
	private sentBatches = 0
	private sentEvents = 0
	private readonly recent: AnalyticsWireEvent[] = []

	constructor(private readonly options: AnalyticsCoreOptions) {
		this.endpoint = `${options.apiUrl}/api/metrics`
		if (typeof window !== "undefined") {
			// pagehide is the reliable "tab is going away" signal on iOS Safari;
			// visibilitychange→hidden catches app switches that never unload
			window.addEventListener("pagehide", () => this.flush())
			document.addEventListener("visibilitychange", () => {
				if (document.visibilityState === "hidden") this.flush()
			})
		}
	}

	get enabled(): boolean {
		return this.options.enabled !== false
	}

	/** §4.3 fallback: consented subject id, HMAC'd server-side. Null clears. */
	setSubject(subject: string | null): void {
		this.subject = subject
	}

	/** Observe every queued event (used by the PostHog sink and dev logging) */
	onEvent(listener: (event: AnalyticsWireEvent) => void): () => void {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	}

	track<E extends AnalyticsEventName>(event: E, props: AnalyticsEventProps[E]): void {
		const wire: AnalyticsWireEvent = { ts: Date.now(), event, props: sanitize(props) }
		if (this.options.debug) {
			console.debug(`[metrics] ${event}`, wire.props)
		}
		for (const listener of this.listeners) {
			try { listener(wire) } catch { /* sinks are best-effort */ }
		}
		this.recent.push(wire)
		if (this.recent.length > 50) this.recent.shift()
		if (!this.enabled) return

		this.queue.push(wire)
		if (this.queue.length > cMaxQueued) {
			this.queue.splice(0, this.queue.length - cMaxQueued)
		}
		if (this.queue.length >= cFlushAtCount) {
			this.flush()
		} else if (!this.timer) {
			this.timer = setTimeout(() => this.flush(), cFlushIntervalMs)
		}
	}

	flush(): void {
		if (this.timer) {
			clearTimeout(this.timer)
			this.timer = null
		}
		if (!this.enabled || this.queue.length === 0) return

		while (this.queue.length > 0) {
			const events = this.queue.splice(0, cAnalyticsLimits.maxEventsPerBatch)
			const batch: AnalyticsBatch = {
				session_id: this.sessionId,
				platform: this.options.platform,
				app_version: this.options.appVersion,
				events,
			}
			if (this.subject) batch.subject = this.subject
			this.sentBatches += 1
			this.sentEvents += events.length
			this.send(JSON.stringify(batch))
		}
	}

	/** Snapshot for dev tooling: queue depth, send counts, recent events */
	snapshot() {
		return {
			sessionId: this.sessionId,
			enabled: this.enabled,
			endpoint: this.endpoint,
			queued: this.queue.length,
			sentBatches: this.sentBatches,
			sentEvents: this.sentEvents,
			recent: [...this.recent],
		}
	}

	private send(body: string): void {
		// text/plain keeps the request CORS-simple (no preflight) so sendBeacon
		// works cross-origin in dev; the server parses the body as JSON
		if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
			try {
				if (navigator.sendBeacon(this.endpoint, new Blob([body], { type: "text/plain;charset=UTF-8" }))) {
					return
				}
			} catch { /* fall through to fetch */ }
		}
		try {
			void fetch(this.endpoint, {
				method: "POST",
				body,
				headers: { "Content-Type": "text/plain;charset=UTF-8" },
				keepalive: true,
				credentials: "omit",
			}).catch(() => undefined)
		} catch { /* best-effort */ }
	}
}

/** Clamp strings/arrays so a bad call site can't bloat a batch */
function sanitize(props: Record<string, unknown>): Record<string, unknown> {
	const out: Record<string, unknown> = {}
	for (const [key, value] of Object.entries(props)) {
		if (value === undefined || value === null) continue
		if (typeof value === "string") {
			out[key] = value.slice(0, cAnalyticsLimits.maxStringLength)
		} else if (Array.isArray(value)) {
			out[key] = (value as unknown[]).slice(0, cAnalyticsLimits.maxArrayLength).map((v: unknown) =>
				typeof v === "string" ? v.slice(0, cAnalyticsLimits.maxStringLength) : v)
		} else if (typeof value === "number") {
			out[key] = Number.isFinite(value) ? Math.round(value * 100) / 100 : 0
		} else {
			out[key] = value
		}
	}
	return out
}

/** Hostname of the referrer if it is another site; undefined otherwise */
export function externalReferrerHost(): string | undefined {
	if (typeof document === "undefined" || !document.referrer) return undefined
	try {
		const url = new URL(document.referrer)
		return url.origin === window.location.origin ? undefined : url.hostname
	} catch {
		return undefined
	}
}
